import { useState } from 'react';
import { adminApi } from './api';
import ModuleQuickCreate from './ModuleQuickCreate';
import ConfirmDialog from './ConfirmDialog';
import { downloadXlsxTemplate } from './downloadXlsxTemplate';

const MAX_JSON = 240 * 1024;
const MAX_EXCEL = 2 * 1024 * 1024;

function downloadBlob(filename, content, type) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function downloadTemplate(modules) {
  const example = {
    version: 1,
    courses: [{
      module_slug: modules[0]?.slug || 'matiere-existante',
      titre: 'Exemple de cours à personnaliser',
      objectif_global: 'Objectif pédagogique',
      access_tier: 'free',
      chapters: [{
        titre: 'Chapitre 1',
        objectif: 'Objectif du chapitre',
        sections: [{
          titre: 'Section 1',
          objectif_pedagogique: 'Compétence visée',
          contenu: 'Contenu pédagogique à personnaliser.',
          notions_cles: ['Notion importante'],
          a_retenir: ['Point essentiel'],
          vigilances: [],
          statut_fiabilite: 'a_verifier',
        }],
      }],
    }],
  };
  downloadBlob('modele-import-cours.json', JSON.stringify(example, null, 2), 'application/json');
}

async function readJsonFile(file) {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
    reader.readAsText(file, 'utf-8');
  });
}

export default function UnifiedCourseImport({ modules = [], onImported, onUnauthorized, onCatalogChanged }) {
  const [format, setFormat] = useState('json');
  const [moduleId, setModuleId] = useState('');
  const [file, setFile] = useState(null);
  const [jsonPayload, setJsonPayload] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheetName, setSheetName] = useState('');
  const [sheetDraft, setSheetDraft] = useState('');
  const [sheetFilter, setSheetFilter] = useState('');
  const [sheetDialog, setSheetDialog] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  function clearFile() {
    setFile(null);
    setJsonPayload(null);
    setSheets([]);
    setSheetName('');
    setSheetDraft('');
    setSheetFilter('');
    setSheetDialog(false);
    setPreview(null);
    setConfirmationOpen(false);
    setError('');
    setSuccess('');
  }

  async function downloadCurrentTemplate() {
    setError('');
    if (format === 'json') {
      downloadTemplate(modules);
      return;
    }
    setBusy(true);
    try {
      await downloadXlsxTemplate('courses');
    } catch (failure) {
      if (failure.status === 401) onUnauthorized?.();
      else setError(failure.message || 'Téléchargement du modèle Excel impossible.');
    } finally {
      setBusy(false);
    }
  }

  function chooseFormat(value) {
    if (busy || value === format) return;
    setFormat(value);
    clearFile();
  }

  async function chooseFile(event) {
    const next = event.target.files?.[0] || null;
    clearFile();
    if (!next) return;
    const isExcel = format === 'excel';
    const extension = isExcel ? '.xlsx' : '.json';
    const max = isExcel ? MAX_EXCEL : MAX_JSON;
    if (!next.name.toLowerCase().endsWith(extension) || next.size > max || next.size === 0) {
      setError('Fichier ' + extension + ' requis, de ' +
        (isExcel ? '2 Mo' : '240 Ko') + ' maximum.');
      return;
    }
    setBusy(true);
    try {
      if (isExcel) {
        const response = await adminApi.inspectWorkbookSheets(next);
        if (!Array.isArray(response.sheets) || !response.sheets.length) {
          throw new Error('Aucune feuille Excel trouvée dans ce fichier.');
        }
        setSheets(response.sheets);
        setSheetDraft(response.sheets[0]);
        setSheetDialog(true);
      } else {
        const data = JSON.parse(await readJsonFile(next));
        if (!data || data.version !== 1 || !Array.isArray(data.courses)) {
          throw new Error('Format JSON attendu : version 1 et tableau « courses ».');
        }
        setJsonPayload(data);
      }
      setFile(next);
    } catch (failure) {
      setError(failure.message || 'Fichier illisible.');
      if (failure.status === 401) onUnauthorized?.();
    } finally {
      setBusy(false);
    }
  }

  function jsonRequest(dryRun) {
    const { target_module_id, target_module_slug, dry_run, ...safe } = jsonPayload;
    return {
      ...safe,
      ...(moduleId ? { target_module_id: Number(moduleId) } : {}),
      dry_run: dryRun,
    };
  }

  async function runImport(dryRun) {
    if (!file || busy || (format === 'json' && !jsonPayload) ||
      (format === 'excel' && (!sheetName || sheetDialog)) || (!dryRun && !preview)) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const result = format === 'json'
        ? await adminApi.importCourses(jsonRequest(dryRun))
        : await adminApi.bulkImportCoursesXlsx(
          file, sheetName, dryRun, moduleId, dryRun ? '' : preview.sha256
        );
      if (dryRun) {
        setPreview(result);
      } else {
        clearFile();
        setSuccess(result.courses + ' cours et ' + result.sections +
          ' sections importés en brouillon. Vous pouvez maintenant les modifier.');
        await onImported?.();
      }
    } catch (failure) {
      setPreview(null);
      if (failure.status === 401) onUnauthorized?.();
      else setError(failure.message || 'Import impossible. Aucun enregistrement partiel.');
    } finally {
      setBusy(false);
    }
  }

  function requestImport(dryRun) {
    if (dryRun) {
      runImport(true);
      return;
    }
    if (!busy && preview && file && !sheetDialog) setConfirmationOpen(true);
  }

  async function confirmImport() {
    if (busy || !confirmationOpen) return;
    try {
      await runImport(false);
    } finally {
      setConfirmationOpen(false);
    }
  }

  const ready = Boolean(file) && !busy && !sheetDialog &&
    (format === 'json' ? Boolean(jsonPayload) : Boolean(sheetName));
  const filteredSheets = sheets.filter((name) =>
    name.toLocaleLowerCase().includes(sheetFilter.toLocaleLowerCase()));

  return (
    <section className="bo-panel bo-course-import bo-course-import--compact"
      aria-label="Importation des cours">
      <div className="bo-course-import__heading">
        <div>
          <h3>Importer des cours</h3>
          <p className="bo-muted">
            Excel ou JSON · Vérification avant import · Publication en brouillon
          </p>
        </div>
        <button type="button" className="bo-button bo-button--ghost"
          onClick={downloadCurrentTemplate} disabled={busy}>
          Télécharger le modèle {format === 'excel' ? 'Excel (.xlsx)' : 'JSON'}
        </button>
      </div>

      <div className="bo-course-import__format" role="group" aria-label="Format du fichier">
        <button type="button" aria-pressed={format === 'excel'}
          disabled={busy} onClick={() => chooseFormat('excel')}
          className={'bo-button ' + (format === 'excel' ? 'bo-button--primary' : 'bo-button--ghost')}>
          Excel (.xlsx)
        </button>
        <button type="button" aria-pressed={format === 'json'}
          disabled={busy} onClick={() => chooseFormat('json')}
          className={'bo-button ' + (format === 'json' ? 'bo-button--primary' : 'bo-button--ghost')}>
          JSON (.json)
        </button>
      </div>

      <div className="bo-course-import__fields">
        <div className="bo-course-import__matter">
          <label htmlFor="bo-course-import-module">Matière de destination</label>
          <select id="bo-course-import-module" className="bo-input" value={moduleId}
            disabled={busy}
            onChange={(event) => { setModuleId(event.target.value); setPreview(null); setConfirmationOpen(false); }}>
            <option value="">Conserver les matières indiquées dans le fichier</option>
            {modules.map((module) => (
              <option key={module.id} value={module.id}>{module.matiere}</option>
            ))}
          </select>
          <ModuleQuickCreate label="+ Créer une matière"
            onCreated={async (module) => {
              setModuleId(String(module.id));
              setPreview(null);
              setConfirmationOpen(false);
              await onCatalogChanged?.();
            }}/>
        </div>
        <div className="bo-course-import__file">
          <label htmlFor="bo-course-import-file">
            Fichier {format === 'excel' ? 'Excel (.xlsx, 2 Mo max.)' : 'JSON (.json, 240 Ko max.)'}
          </label>
          <input key={format} id="bo-course-import-file" className="bo-input"
            type="file" accept={format === 'excel'
              ? '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              : '.json,application/json'}
            disabled={busy} onChange={chooseFile}/>
          {format === 'excel' && sheetName && (
            <p className="bo-course-import__sheet-selected">
              Feuille : <strong>{sheetName}</strong>
              <button type="button" className="bo-button bo-button--ghost"
                onClick={() => { setSheetDraft(sheetName); setSheetFilter('');
                  setSheetDialog(true); setPreview(null); setConfirmationOpen(false); }}>
                Changer de feuille
              </button>
            </p>
          )}
        </div>
      </div>

      <div className="bo-course-import__actions">
        <p className="bo-muted">
          {format === 'excel'
            ? 'Une ligne par section. La feuille Excel sera choisie avant vérification.'
            : 'Le fichier JSON décrit les cours, chapitres et sections.'}
          {' '}La matière choisie ici remplace celle du fichier.
        </p>
        <button type="button" className="bo-button bo-button--secondary"
          disabled={!ready} onClick={() => requestImport(true)}>
          {busy ? 'Vérification…' : 'Vérifier sans importer'}
        </button>
      </div>

      {preview && (
        <div className="bo-course-import__confirm" role="status">
          <span><strong>{preview.courses} cours</strong>, {preview.chapters} chapitres,
            {' '}{preview.sections} sections vérifiés
            {sheetName ? ' · Feuille : ' + sheetName : ''}.</span>
          <button type="button" className="bo-button bo-button--primary"
            disabled={busy} onClick={() => requestImport(false)}>
            Continuer vers la confirmation
          </button>
        </div>
      )}
      {error && <p role="alert" className="bo-alert bo-alert--error">{error}</p>}
      {success && <p role="status" className="bo-alert bo-alert--success">{success}</p>}

      <ConfirmDialog
        open={confirmationOpen}
        title="Confirmer l'importation des cours"
        description="Ces contenus seront enregistrés en brouillon. Ils pourront être vérifiés et modifiés avant leur publication."
        details={preview && (
          <dl className="bo-confirm-dialog__summary">
            <div><dt>Cours</dt><dd>{preview.courses}</dd></div>
            <div><dt>Chapitres</dt><dd>{preview.chapters}</dd></div>
            <div><dt>Sections</dt><dd>{preview.sections}</dd></div>
            <div><dt>Format</dt><dd>{format === 'excel' ? 'Excel (.xlsx)' : 'JSON (.json)'}</dd></div>
            {sheetName && <div><dt>Feuille sélectionnée</dt><dd>{sheetName}</dd></div>}
            {moduleId && <div><dt>Matière de destination</dt>
              <dd>{modules.find((module) => String(module.id) === moduleId)?.matiere || 'Matière sélectionnée'}</dd></div>}
          </dl>
        )}
        confirmLabel="Confirmer l'import en brouillon"
        busy={busy}
        onCancel={() => setConfirmationOpen(false)}
        onConfirm={confirmImport}
      />

      {sheetDialog && (
        <div className="bo-panel bo-sheet-choice" role="dialog" aria-modal="true"
          aria-labelledby="course-import-sheet-title">
          <h4 id="course-import-sheet-title">Choisir la feuille Excel</h4>
          <p className="bo-muted">Seule la feuille sélectionnée sera analysée et importée.</p>
          <label htmlFor="course-import-sheet-filter">Filtrer les feuilles par nom</label>
          <input id="course-import-sheet-filter" className="bo-input" type="search"
            placeholder="Rechercher un onglet…" value={sheetFilter}
            onChange={(event) => setSheetFilter(event.target.value)}/>
          <label htmlFor="course-import-sheet-select">Feuille du classeur</label>
          <select id="course-import-sheet-select" className="bo-input"
            value={filteredSheets.includes(sheetDraft) ? sheetDraft : ''}
            onChange={(event) => setSheetDraft(event.target.value)}>
            {!filteredSheets.includes(sheetDraft) && <option value="">Choisir une feuille</option>}
            {filteredSheets.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
          {!filteredSheets.length && <p>Aucune feuille ne correspond à la recherche.</p>}
          <div className="bo-actions">
            <button type="button" className="bo-button bo-button--primary"
              disabled={!filteredSheets.includes(sheetDraft)}
              onClick={() => { setSheetName(sheetDraft); setSheetDialog(false);
                setPreview(null); setConfirmationOpen(false); }}>
              Utiliser cette feuille
            </button>
            <button type="button" className="bo-button bo-button--ghost"
              onClick={() => { if (!sheetName) clearFile(); else setSheetDialog(false); }}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
